/* Fragapalooza Calendar v2 — SPA logic (TypeScript).
 *
 * - Loads events from /api/calendar via AJAX and polls every POLL_MS using an
 *   ETag so unchanged data returns a cheap 304.
 * - Renders with FullCalendar 6. Overlapping events use FullCalendar's
 *   side-by-side timegrid layout.
 * - View switcher (full event / day / list) and group-filter chips work
 *   entirely client-side — no page reloads.
 * - eventClick opens an accessible modal (focus trap, ESC/overlay close).
 * - Times always render in the event's Edmonton (Mountain) timezone — not the
 *   viewer's local zone — since the event is on-site; the footer says so.
 */
import { Calendar } from "@fullcalendar/core";
import type { EventClickArg, EventInput } from "@fullcalendar/core";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import luxon3Plugin from "@fullcalendar/luxon3";

import type { ApiEvent, CalendarPayload, GroupMeta, ViewName } from "./types";
import "./styles.css";

const POLL_MS = 25_000;

interface State {
  etag: string | null;
  payload: CalendarPayload | null;
  enabledGroups: Set<string>;
  calendar: Calendar | null;
  dayCount: number;
  windowStart: string | null;
  timezone: string;
}

const state: State = {
  etag: null,
  payload: null,
  enabledGroups: new Set<string>(),
  calendar: null,
  dayCount: 4,
  windowStart: null,
  // The event is always in Edmonton, so times render in that fixed zone rather
  // than the viewer's local zone (overridden from the payload on first load).
  timezone: "America/Edmonton",
};

// ---- DOM helpers -----------------------------------------------------------

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node as T;
}

function setStatus(msg: string, isError = false): void {
  const s = el("status");
  s.textContent = msg;
  s.classList.toggle("error", isError);
}

// ---- data loading ----------------------------------------------------------

async function fetchCalendar(): Promise<void> {
  const headers: Record<string, string> = {};
  if (state.etag) headers["If-None-Match"] = state.etag;

  try {
    const res = await fetch("/api/calendar", { headers, cache: "no-store" });
    if (res.status === 304) {
      setStatus("");
      return;
    }
    if (res.status === 503) {
      setStatus("Calendar is starting up…");
      return;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    state.etag = res.headers.get("ETag");
    const data = (await res.json()) as CalendarPayload;
    applyPayload(data);
    setStatus("");
  } catch (err) {
    setStatus("Couldn't reach the calendar. Retrying…", true);
    console.error("[fpcal] fetch failed", err);
  }
}

function applyPayload(payload: CalendarPayload): void {
  const firstLoad = state.payload === null;
  state.payload = payload;
  state.dayCount = payload.window?.day_count ?? 4;
  state.windowStart = payload.window?.start ?? null;
  state.timezone = payload.timezone || state.timezone;

  if (firstLoad) {
    payload.groups.forEach((g) => state.enabledGroups.add(g.id));
    buildFilters(payload.groups);
    buildCalendar();
  } else {
    state.calendar?.refetchEvents();
  }
  renderUpdated(payload.updated_at);
}

function renderUpdated(iso: string | null): void {
  if (!iso) return;
  const d = new Date(iso);
  el("updated").textContent =
    "Last Updated " +
    d.toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
}

// ---- event source ----------------------------------------------------------

function visibleEvents(): EventInput[] {
  if (!state.payload) return [];
  return state.payload.events
    .filter((e) => state.enabledGroups.has(e.group_id))
    .map((e) => ({
      id: e.id,
      title: e.title,
      start: e.start,
      end: e.end,
      allDay: e.all_day,
      backgroundColor: e.color,
      borderColor: e.color,
      textColor: "#ffffff",
      extendedProps: e as unknown as Record<string, unknown>,
    }));
}

// ---- calendar --------------------------------------------------------------

function buildCalendar(): void {
  const target = el("calendar");
  const phone = isPhone();
  const isNarrow = phone || window.matchMedia("(max-width: 640px)").matches;
  const dayCount = state.dayCount;

  state.calendar = new Calendar(target, {
    plugins: [timeGridPlugin, listPlugin, luxon3Plugin],
    // Always show the event's Edmonton time, not the viewer's local zone. The
    // luxon plugin resolves the named zone (with DST) via the browser's Intl.
    timeZone: state.timezone,
    initialView: isNarrow ? "listEvent" : "timeGridEvent",
    initialDate: state.windowStart ?? undefined,
    headerToolbar: { left: "title", right: "prev,next today" },
    // Time-grid views get a bounded height (set per-view in applyHeight) so they
    // scroll internally, which lets FullCalendar freeze the day-column headers
    // (stickyHeaderDates). List view falls back to height:"auto" (page scroll).
    height: "auto",
    expandRows: false,
    stickyHeaderDates: true,
    nowIndicator: true,
    allDaySlot: false, // no all-day events in this feed — reclaim the vertical space
    slotEventOverlap: false, // side-by-side, not stacked-on-top
    eventMaxStack: 6,
    displayEventEnd: true,
    // Lift very short events to a floor tall enough to show a wrapped title
    // (the title fills the block — see styles.css). Phones get a taller floor
    // since their single wide column wraps long titles onto more lines. The
    // full title is always available via hover tooltip + the detail modal.
    eventMinHeight: phone ? 72 : 50,
    views: {
      timeGridEvent: { type: "timeGrid", duration: { days: dayCount } },
      listEvent: { type: "list", duration: { days: dayCount } },
    },
    events: (_info, success) => success(visibleEvents()),
    eventClick: (arg: EventClickArg) => {
      arg.jsEvent.preventDefault();
      openModal(arg.event.extendedProps as unknown as ApiEvent);
    },
    eventDidMount: (arg) => {
      // Native tooltip: full title on hover, covering titles still clamped.
      arg.el.setAttribute("title", arg.event.title);
    },
  });
  state.calendar.render();
  applyHeight();
  requestAnimationFrame(() => {
    measureFcToolbar();
    applyHeight();
  });

  // The 4-day "Calendar (Full)" grid is unusable on a phone-sized screen, so
  // drop that option entirely on phones (Day + List remain). Non-phones — even
  // a narrowed desktop window — keep it. Inline display beats the .chip rule.
  if (phone) {
    document
      .querySelector<HTMLElement>('.view-btn[data-view="event"]')
      ?.style.setProperty("display", "none");
  }
  if (isNarrow) markActiveView("list");
}

/** True for actual phones (not just narrow windows), so a desktop with a small
 *  window still gets the full-calendar option. Prefers UA Client Hints; falls
 *  back to touch input + a phone-sized physical screen (iOS/Safari, older). */
function isPhone(): boolean {
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } })
    .userAgentData;
  if (uaData && typeof uaData.mobile === "boolean") return uaData.mobile;
  const minSide = Math.min(window.screen.width, window.screen.height);
  return window.matchMedia("(pointer: coarse)").matches && minSide <= 480;
}

// ---- view switching --------------------------------------------------------

function clampToWindow(): Date {
  if (!state.payload?.window) return new Date();
  const start = new Date(state.payload.window.start);
  const end = new Date(state.payload.window.end);
  const now = new Date();
  if (now < start || now >= end) return start;
  return now;
}

function markActiveView(view: ViewName): void {
  document.querySelectorAll<HTMLButtonElement>(".view-btn").forEach((b) => {
    b.classList.toggle("is-active", b.dataset.view === view);
  });
}

function switchView(view: ViewName): void {
  const cal = state.calendar;
  if (!cal) return;

  if (view === "event") {
    cal.changeView("timeGridEvent", state.windowStart ?? undefined);
  } else if (view === "day") {
    cal.changeView("timeGridDay", clampToWindow());
  } else {
    cal.changeView("listEvent", state.windowStart ?? undefined);
  }
  applyHeight();
  if (view === "day") scrollNowIntoView();
  markActiveView(view);
  requestAnimationFrame(() => {
    measureFcToolbar();
    applyHeight();
  });
}

/** Scroll the page so the current-time indicator sits in the vertical centre of
 *  the viewport. No-op when "now" is outside the displayed day (e.g. viewing the
 *  event before it starts) — the day grid then just shows from the top. The
 *  grid renders at natural height (height: "auto"), so this scrolls the window,
 *  not an inner scroller. Double rAF waits for FullCalendar to paint the line. */
function scrollNowIntoView(): void {
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const line = document.querySelector<HTMLElement>(
        ".fc-timegrid-now-indicator-line",
      );
      line?.scrollIntoView({ block: "center", behavior: "auto" });
    }),
  );
}

// ---- group filters ---------------------------------------------------------

function buildFilters(groups: GroupMeta[]): void {
  const wrap = el("group-filters");
  wrap.innerHTML = "";

  // "All" master chip — selects every group at once.
  const allBtn = document.createElement("button");
  allBtn.type = "button";
  allBtn.className = "chip filter-btn filter-all";
  allBtn.dataset.group = "__all__";
  allBtn.textContent = "All";
  allBtn.addEventListener("click", () => {
    groups.forEach((g) => state.enabledGroups.add(g.id));
    syncFilterChips(groups);
    state.calendar?.refetchEvents();
  });
  wrap.appendChild(allBtn);

  groups.forEach((g) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "chip filter-btn";
    btn.dataset.group = g.id;

    const swatch = document.createElement("span");
    swatch.className = "swatch";
    swatch.style.background = g.color;
    btn.appendChild(swatch);
    btn.appendChild(document.createTextNode(g.label));

    btn.addEventListener("click", () => {
      const on = btn.getAttribute("aria-pressed") === "true";
      if (on) {
        state.enabledGroups.delete(g.id);
      } else {
        state.enabledGroups.add(g.id);
      }
      syncFilterChips(groups);
      state.calendar?.refetchEvents();
    });
    wrap.appendChild(btn);
  });

  syncFilterChips(groups);
}

/** Reflect state.enabledGroups onto the group chips and the "All" chip. */
function syncFilterChips(groups: GroupMeta[]): void {
  const allOn = groups.every((g) => state.enabledGroups.has(g.id));
  document
    .querySelectorAll<HTMLButtonElement>("#group-filters .filter-btn")
    .forEach((btn) => {
      const gid = btn.dataset.group;
      const pressed =
        gid === "__all__" ? allOn : !!gid && state.enabledGroups.has(gid);
      btn.setAttribute("aria-pressed", pressed ? "true" : "false");
    });
}

// ---- modal (accessible) ----------------------------------------------------

let modalReturnFocus: HTMLElement | null = null;

function openModal(ev: ApiEvent): void {
  el("modal-title").textContent = ev.title || "Event";
  const groupEl = el("modal-group");
  groupEl.textContent = ev.group_label || "";
  groupEl.style.background = ev.color || "var(--accent)";
  el("modal-when").textContent = formatWhen(ev);

  toggleRow("modal-location-row", "modal-location", ev.location);
  toggleRow("modal-description-row", "modal-description", ev.description);

  modalReturnFocus = document.activeElement as HTMLElement | null;
  el("modal-overlay").hidden = false;
  el("event-modal").hidden = false;
  el("modal-close").focus();
  document.addEventListener("keydown", onModalKeydown, true);
}

function closeModal(): void {
  el("modal-overlay").hidden = true;
  el("event-modal").hidden = true;
  document.removeEventListener("keydown", onModalKeydown, true);
  modalReturnFocus?.focus();
  modalReturnFocus = null;
}

function onModalKeydown(e: KeyboardEvent): void {
  if (e.key === "Escape") {
    e.preventDefault();
    closeModal();
    return;
  }
  if (e.key !== "Tab") return;
  const modal = el("event-modal");
  const focusable = Array.from(
    modal.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
  ).filter((n) => !n.hidden && n.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

function toggleRow(rowId: string, valId: string, value?: string | null): void {
  const row = el(rowId);
  if (value) {
    el(valId).textContent = value;
    row.hidden = false;
  } else {
    row.hidden = true;
  }
}

function formatWhen(ev: ApiEvent): string {
  const start = new Date(ev.start);
  const end = ev.end ? new Date(ev.end) : null;
  const dateOpts: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric" };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };

  if (ev.all_day) return start.toLocaleDateString([], dateOpts);

  let s = start.toLocaleDateString([], dateOpts) + ", " + start.toLocaleTimeString([], timeOpts);
  if (end) {
    const sameDay = start.toDateString() === end.toDateString();
    s +=
      " – " +
      (sameDay ? "" : end.toLocaleDateString([], dateOpts) + ", ") +
      end.toLocaleTimeString([], timeOpts);
  }
  return s;
}

// ---- theme -----------------------------------------------------------------

function initTheme(): void {
  const saved = localStorage.getItem("fpcal-theme");
  if (saved) document.documentElement.setAttribute("data-theme", saved);
  el("theme-toggle").addEventListener("click", () => {
    const cur = document.documentElement.getAttribute("data-theme");
    const isDark =
      cur === "dark" || (!cur && window.matchMedia("(prefers-color-scheme: dark)").matches);
    const next = isDark ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("fpcal-theme", next);
  });
}

// ---- wire up ---------------------------------------------------------------

/** Publish the (sticky) app toolbar's height as --toolbar-h so the frozen
 *  calendar toolbar and list day header can stack beneath it. Tracked live
 *  because the toolbar wraps taller on narrow/mobile widths and grows when
 *  filter chips load. */
function trackToolbarHeight(): void {
  const toolbar = document.querySelector<HTMLElement>(".toolbar");
  if (!toolbar) return;
  const apply = () =>
    document.documentElement.style.setProperty("--toolbar-h", `${toolbar.offsetHeight}px`);
  apply();
  new ResizeObserver(apply).observe(toolbar);
}

/** Publish the frozen FullCalendar toolbar's height as --fc-toolbar-h so the
 *  list day header sticks below it (not behind it). Re-measured after each view
 *  change/resize since the toolbar can wrap on narrow widths. */
function measureFcToolbar(): void {
  const fcToolbar = document.querySelector<HTMLElement>(".fc-header-toolbar");
  if (fcToolbar) {
    document.documentElement.style.setProperty("--fc-toolbar-h", `${fcToolbar.offsetHeight}px`);
  }
}

/** Time-grid views get a viewport-bounded height so the grid scrolls internally
 *  and its day-column headers freeze (stickyHeaderDates). List view scrolls with
 *  the page (height:"auto"). Height is derived from the calendar's document
 *  offset so header + toolbar + grid + footer fit without a page scrollbar. */
function applyHeight(): void {
  const cal = state.calendar;
  if (!cal) return;
  if (cal.view.type === "listEvent") {
    cal.setOption("height", "auto");
    return;
  }
  const calEl = el("calendar");
  const calTopDoc = calEl.getBoundingClientRect().top + window.scrollY;
  const footerH = document.querySelector<HTMLElement>(".site-footer")?.offsetHeight ?? 40;
  // Fill down to just above the footer — small fudge only for #calendar's
  // padding/border, so the grid doesn't leave a gap above the footer.
  const h = window.innerHeight - calTopDoc - footerH - 8;
  cal.setOption("height", Math.max(420, h));
}

function init(): void {
  initTheme();
  trackToolbarHeight();
  window.addEventListener("resize", () => {
    measureFcToolbar();
    applyHeight();
  });
  document.querySelectorAll<HTMLButtonElement>(".view-btn").forEach((b) => {
    b.addEventListener("click", () => switchView(b.dataset.view as ViewName));
  });
  el("modal-close").addEventListener("click", closeModal);
  el("modal-overlay").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeModal();
  });

  setStatus("Loading…");
  void fetchCalendar().then(() => {
    window.setInterval(fetchCalendar, POLL_MS);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
